import type { Request } from 'express';
import { Location } from 'history';

import { ApiTrainingSearchRetrieveQueryResult } from '@rover/rsdk/src/apiClient/latest/api-training-search-retrieve';
import { ApiTrainingSearchRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiTrainingSearchRetrieveParams';
import { TrainingSearchQuery } from '@rover/rsdk/src/apiClient/latest/model/trainingSearchQuery';
import { snakeifyString } from '@rover/rsdk/src/modules/Network/object-utilities';
import { filterNullishProperties } from '@rover/utilities/object';

import {
  INCLUDE_NEARBY_TRAINER_COUNT,
  NEARBY_TRAINER_COUNT,
  SEARCH_ORIGIN_CENTERLAT,
  SEARCH_ORIGIN_CENTERLNG,
} from '../constants/params';
import { setSkillAndBehaviorQueryParams, SkillsAndBehaviorSlugsDictionary } from '../utils/params';

import { encodeCoordinate, parseCoordinate } from './utilities';

export { parseCoordinate };

/**
 * The subset of the API query params that the trainer search page actually uses.
 * Currently, this only excludes the zoomlevel param, since we don't have a map.
 */
export type TrainerSearchPageApiQueryParams = Pick<
  ApiTrainingSearchRetrieveParams,
  | 'centerlat'
  | 'centerlng'
  | 'location'
  | 'locationAccuracy'
  | 'locationType'
  | 'rawLocationTypes'
  | 'seoLocationSlug'
>;

/**
 * Parses the URL query string into the query params we forward to the API.
 *
 * If you update this, please update parseServerQueryParamsForApi as well.
 */
export function parseUrlQueryParamsForApi(
  urlQueryString: Location['search']
): TrainerSearchPageApiQueryParams {
  if (!urlQueryString) {
    return {};
  }

  const params = new URLSearchParams(urlQueryString);

  const centerlat = params.get('centerlat');
  const centerlng = params.get('centerlng');
  const location = params.get('location');
  const locationAccuracy = params.get('location_accuracy');
  const locationType = params.get('location_type');
  const rawLocationTypes = params.getAll('raw_location_types');
  const seoLocationSlug = params.get('seo_location_slug');

  return filterNullishProperties({
    centerlat: parseCoordinate(centerlat),
    centerlng: parseCoordinate(centerlng),
    location,
    locationAccuracy,
    locationType,
    rawLocationTypes: rawLocationTypes.length > 0 ? rawLocationTypes : undefined,
    seoLocationSlug,
  });
}

function parseRequestQueryParamArray(value: Request['query'][string]): string[] | undefined {
  if (typeof value === 'string') return [value];

  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value;
  }

  return undefined;
}

/**
 * Parses the Express request query object during the server-side render into the query
 * params we forward to the API during server-side prefetch. There's a lot of overlap
 * between here and the parseUrlQueryParamsForApi, but a separate function is warranted
 * since the input types are slightly different and the overlapping logic is trivial.
 *
 * The seemingly heavy-handed runtime type checking is due to the ParsedQs type from Express.
 *
 * If you update this, please update parseUrlQueryParamsForApi as well.
 */
export function parseServerQueryParamsForApi(
  query: Request['query']
): ApiTrainingSearchRetrieveParams {
  const {
    centerlat,
    centerlng,
    location,
    location_accuracy: locationAccuracy,
    location_type: locationType,
    raw_location_types: rawLocationTypes,
    seo_location_slug: seoLocationSlug,
  } = query;

  return filterNullishProperties({
    centerlat: parseCoordinate(centerlat),
    centerlng: parseCoordinate(centerlng),
    location: typeof location === 'string' ? location : undefined,
    locationAccuracy: typeof locationAccuracy === 'string' ? locationAccuracy : undefined,
    locationType: typeof locationType === 'string' ? locationType : undefined,
    rawLocationTypes: parseRequestQueryParamArray(rawLocationTypes),
    seoLocationSlug: typeof seoLocationSlug === 'string' ? seoLocationSlug : undefined,
  });
}

/**
 * The inverse of parseUrlQueryParamsForApi. Encodes the training search API query params
 * into the URLSearchParams format for appending to the page's URL. This can be used when
 * syncing the query present in the API response (which may have server-side defaults applied)
 * back into the query params for the page URL. It can also be used when constructing URLs
 * for client-side navigation to the trainer search page.
 */
export function encodeApiQueryParamsForPageUrl(
  apiQueryParams: TrainerSearchPageApiQueryParams
): URLSearchParams {
  const urlSearchParams = new URLSearchParams();

  // Remove any nullish, empty string, or empty array params, and also convert numbers to strings.
  const preppedParams: Record<string, string | string[]> = filterNullishProperties({
    centerlat: encodeCoordinate(apiQueryParams.centerlat),
    centerlng: encodeCoordinate(apiQueryParams.centerlng),
    location: apiQueryParams.location || undefined,
    locationAccuracy: apiQueryParams.locationAccuracy || undefined,
    locationType: apiQueryParams.locationType || undefined,
    rawLocationTypes: apiQueryParams.rawLocationTypes?.length
      ? apiQueryParams.rawLocationTypes
      : undefined,
    seoLocationSlug: apiQueryParams.seoLocationSlug || undefined,
  });

  Object.entries(preppedParams).forEach(([key, value]) => {
    const snakifiedKey = snakeifyString(key);
    if (Array.isArray(value)) {
      value.forEach((item) => urlSearchParams.append(snakifiedKey, item));
    } else {
      urlSearchParams.set(snakifiedKey, value);
    }
  });

  return urlSearchParams;
}

/**
 * Extracts any UTM tracking params from the URL query string. Client-side only.
 */
export const getUtmURLSearchParams = (urlQueryString: Location['search']): URLSearchParams => {
  const currentParams = new URLSearchParams(urlQueryString);
  const utmParams = new URLSearchParams();

  for (const [key, value] of currentParams.entries()) {
    if (key.startsWith('utm_')) {
      utmParams.set(key, value);
    }
  }

  return utmParams;
};

/**
 * Returns true if the URL query string contains any UTM tracking params.
 */
export const hasUtmParams = (urlQueryString: Location['search']): boolean => {
  return [...getUtmURLSearchParams(urlQueryString).keys()].length > 0;
};

/**
 * Replaces the browser URL with the API result query params, preserving existing UTM params.
 * Client-side only.
 *
 * Note, we do not sync the skill and behavior params as these are only read from the URL
 * on page load to set the initial state of the skill and behavior filters.
 * */
export const syncUrlQueryParams = (
  apiResultQuery: ApiTrainingSearchRetrieveQueryResult['query'],
  urlQueryString: Location['search']
): void => {
  if (!process.env.JS_ENV_CLIENT) {
    return;
  }

  const apiQueryURLSearchParams = encodeApiQueryParamsForPageUrl(apiResultQuery);
  const existingUtmParams = getUtmURLSearchParams(urlQueryString);
  const newSearchParams = new URLSearchParams([...apiQueryURLSearchParams, ...existingUtmParams]);

  const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`;
  window.history.replaceState(null, '', newUrl);
};

interface GetQueryStringForTrainerSearchCardsOptions {
  searchPageQueryString: string;
  selectedSkillsAndBehaviors: SkillsAndBehaviorSlugsDictionary;
  nearbyTrainerCount: number;
  searchQuery: TrainingSearchQuery;
}

/**
 * Builds the query string appended to the href of each trainer card on the trainer
 * search page. Returns an empty string when no params apply.
 *
 * IMPORTANT: the query params built here are for the public trainer profile page
 * — i.e. the destination of the card click — NOT for the trainer search page
 * itself.
 */
export const getQueryStringForTrainerSearchCards = ({
  searchPageQueryString,
  selectedSkillsAndBehaviors,
  nearbyTrainerCount,
  searchQuery,
}: GetQueryStringForTrainerSearchCardsOptions): string => {
  const searchPageQueryParams = new URLSearchParams(searchPageQueryString);
  const profileQueryParams = setSkillAndBehaviorQueryParams(
    new URLSearchParams(),
    selectedSkillsAndBehaviors
  );

  const searchOriginCenterlat = encodeCoordinate(searchQuery.centerlat);
  const searchOriginCenterlng = encodeCoordinate(searchQuery.centerlng);
  if (typeof searchOriginCenterlat === 'string' && typeof searchOriginCenterlng === 'string') {
    profileQueryParams.set(SEARCH_ORIGIN_CENTERLAT, searchOriginCenterlat);
    profileQueryParams.set(SEARCH_ORIGIN_CENTERLNG, searchOriginCenterlng);
  }

  const shouldSendTrainerCountToProfile =
    searchPageQueryParams.get(INCLUDE_NEARBY_TRAINER_COUNT) === 'true';
  if (shouldSendTrainerCountToProfile && nearbyTrainerCount > 0) {
    profileQueryParams.set(NEARBY_TRAINER_COUNT, String(nearbyTrainerCount));
  }

  return profileQueryParams.toString();
};
